import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { Tunables } from '../sim/tunables';
import {
  type Action,
  type AthleteRig,
  type KitLogos,
  type SquadSpec,
  blobTexture,
  helmetGeo,
  liftDark,
  maskGeo,
  numberAtlas,
  rimShader,
  stripeGeo,
} from './athletes';
import type { AthleteModel } from './athleteModel';
import { type DanceArm, danceAt } from './celebration';
import { stepHeading } from './heading';

/**
 * Rigged athletes (GDD section 10): one skinned CC0 body per player, coloured by kit region in the shader,
 * driven by a few clips (idle, jog, sprint, crouch, push, fall) blended by speed and action, with the arms
 * aimed in code on top for the football moves the clips do not have (carry, throw, catch, reach).
 * Helmet, shoulder pads and numbers are rigid instanced meshes that follow the head and chest bones.
 */

type Clip = 'idle' | 'jog' | 'sprint' | 'crouch' | 'push' | 'fall';
const CLIPS: readonly Clip[] = ['idle', 'jog', 'sprint', 'crouch', 'push', 'fall'];
/** Kit regions: skin, jersey, pants, socks, glove, cleat (see kitCuts). */
const KIT_REGIONS = 6;

type GearName = 'helmet' | 'stripe' | 'mask' | 'pads' | 'numFront' | 'numBack' | 'shoeL' | 'shoeR';
type GearBone = 'head' | 'chest' | 'footL' | 'footR';
type LogoKind = 'word' | 'mark';
/** One logo patch on the kit: which artwork, which bone it rides on, and where relative to that bone. */
interface LogoSlot {
  kind: LogoKind;
  bone: GearBone;
  local: THREE.Matrix4;
}
type V3 = readonly [number, number, number];
/**
 * Arm aims in the torso's frame: x away from the body on that arm's side, y up the spine, z out of the chest.
 * `ground` aims in the upright body frame instead (a hand to the turf).
 */
interface ArmAim {
  up: V3;
  lo: V3;
  ground?: boolean;
}
interface Overlay {
  left: ArmAim | null;
  right: ArmAim | null;
}
/** Where an arm is aimed right now: eases toward each new ArmAim so poses blend, never pop. */
interface ArmPose {
  up: THREE.Vector3;
  lo: THREE.Vector3;
  ground: boolean;
}

const NO_OVERLAY: Overlay = { left: null, right: null };
/** Ball tucked high in the left arm. */
const TUCK: ArmAim = { up: [0.2, -0.75, 0.35], lo: [-0.75, 0.2, 0.6] };
const HANDS_UP: ArmAim = { up: [0.25, 0.9, 0.35], lo: [0.05, 0.95, 0.3] };
const REACH: ArmAim = { up: [0.2, 0.3, 0.95], lo: [0.05, 0.4, 0.95] };
const READY: ArmAim = { up: [0.15, -0.55, 0.75], lo: [-0.45, 0.25, 0.85] };
const HAND_DOWN: ArmAim = { up: [0.05, -0.75, 0.6], lo: [0, -1, 0.1], ground: true };
const DIVE: ArmAim = { up: [0.15, 0.1, 1], lo: [0.05, 0.1, 1] };
const THROW_WIND: ArmAim = { up: [0.75, 0.45, -0.5], lo: [0.1, 1, -0.35] };
const THROW_RELEASE: ArmAim = { up: [0.3, 0.6, 0.75], lo: [0, 0.45, 1] };
const THROW_FOLLOW: ArmAim = { up: [-0.25, -0.45, 0.85], lo: [-0.5, -0.55, 0.6] };
/** Pass pro: hands at the chest, punching forward (the push clip alone holds them high). */
const BLOCK: ArmAim = { up: [0.3, -0.35, 0.9], lo: [-0.25, 0.25, 1] };
const GUIDE_HAND: ArmAim = { up: [0.25, 0.05, 1], lo: [-0.35, 0.1, 1] };
/** Touchdown dance arms (see celebration.ts). */
const DANCE_ARMS: Record<DanceArm, ArmAim> = {
  pumpUp: { up: [0.55, 0.75, 0.35], lo: [0.05, 1, 0.15] },
  pumpDown: { up: [0.45, -0.35, 0.6], lo: [-0.3, 0.55, 0.8] },
  out: { up: [1, 0.2, 0.05], lo: [1, 0.3, 0.1] },
  flex: { up: [1, 0.15, 0.15], lo: [0.15, 1, 0.1] },
};

interface Bones {
  head: THREE.Bone;
  chest: THREE.Bone;
  footL: THREE.Bone;
  footR: THREE.Bone;
  upL: THREE.Bone;
  loL: THREE.Bone;
  handL: THREE.Bone;
  palmL: THREE.Bone;
  upR: THREE.Bone;
  loR: THREE.Bone;
  handR: THREE.Bone;
  palmR: THREE.Bone;
}

interface Rigged {
  holder: THREE.Group;
  mixer: THREE.AnimationMixer;
  actions: Record<Clip, THREE.AnimationAction>;
  weights: Record<Clip, number>;
  bones: Bones;
  x: number;
  z: number;
  px: number;
  pz: number;
  yaw: number;
  faceYaw: number | null;
  /** Smoothed ground velocity (yards a second), see stepHeading. */
  vx: number;
  vz: number;
  speed: number;
  /** Run cycle position, 0 to 1. */
  cycle: number;
  action: Action;
  actionT: number;
  pitch: number;
  lift: number;
  /** The dance's turn and sway on top of the facing (zero outside a celebration). */
  danceYaw: number;
  roll: number;
  overlayL: number;
  overlayR: number;
  poseL: ArmPose;
  poseR: ArmPose;
  /** The clip's own rotation of the four arm bones, kept so the aim never builds on last frame's aim. */
  armClip: THREE.Quaternion[];
  visible: boolean;
}

const v = new THREE.Vector3();
const v2 = new THREE.Vector3();
const qa = new THREE.Quaternion();
const qb = new THREE.Quaternion();
const qc = new THREE.Quaternion();
const qHolder = new THREE.Quaternion();
const qTorso = new THREE.Quaternion();
const euler = new THREE.Euler(0, 0, 0, 'YXZ');

export class SkinnedAthletesView implements AthleteRig {
  readonly group = new THREE.Group();
  private readonly list: Rigged[] = [];
  private readonly offsets: number[] = [];
  private readonly gear = {} as Record<GearName, THREE.InstancedMesh>;
  /** Gear placement relative to its bone, measured once in the bind pose. */
  private readonly gearLocal = {} as Record<GearName, THREE.Matrix4>;
  private readonly gearBone = {} as Record<GearName, GearBone>;
  private readonly shadow: THREE.InstancedMesh;
  private readonly logoMeshes: Partial<Record<LogoKind, THREE.InstancedMesh>> = {};
  private readonly logoSlots: LogoSlot[] = [];
  /** Per athlete: wears the brand logos. */
  private readonly branded: boolean[] = [];
  private readonly materials: THREE.Material[] = [];
  /** Bone axis (toward its child) in the bone's own frame, for aiming. */
  private readonly axisUpL = new THREE.Vector3();
  private readonly axisLoL = new THREE.Vector3();
  private readonly axisUpR = new THREE.Vector3();
  private readonly axisLoR = new THREE.Vector3();
  /** Inverse of the chest bone's bind rotation: turns the chest's rotation into the torso frame. */
  private readonly chestBindInv = new THREE.Quaternion();
  private readonly strideJog: number;
  private readonly strideSprint: number;
  private readonly speedJog: number;
  private readonly speedSprint: number;
  private readonly m = new THREE.Matrix4();
  private readonly hidden = new THREE.Matrix4().makeScale(0, 0, 0);

  constructor(
    squads: SquadSpec[],
    private readonly t: Tunables,
    model: AthleteModel,
    logos: KitLogos = { word: null, mark: null },
  ) {
    const A = t.athlete;
    const total = squads.reduce((n, s) => n + s.numbers.length, 0);
    const clips = {} as Record<Clip, THREE.AnimationClip>;
    for (const c of CLIPS) {
      const clip = model.clips.get(c);
      if (!clip) throw new Error(`athlete model has no ${c} clip`);
      clips[c] = clip;
    }
    this.strideJog = A.jogStrideMetres * A.scale;
    this.strideSprint = A.sprintStrideMetres * A.scale;
    this.speedJog = this.strideJog / clips.jog.duration;
    this.speedSprint = this.strideSprint / clips.sprint.duration;

    // Bind-pose frames for the gear and the arm axes, from the template.
    const template = model.scene;
    template.updateMatrixWorld(true);
    const tb = findBones(template);
    this.axisUpL.copy(tb.loL.position).normalize();
    this.axisLoL.copy(tb.handL.position).normalize();
    this.axisUpR.copy(tb.loR.position).normalize();
    this.axisLoR.copy(tb.handR.position).normalize();
    tb.chest.getWorldQuaternion(this.chestBindInv).invert();
    const hd = model.variant === 'hd';
    // The HD athlete has its own helmet, mask, pads and cleats, and its own proportions for the numbers and logos.
    const g = hd ? { ...A.gear, ...A.hd.gear, logos: { ...A.gear.logos, ...A.hd.gear.logos } } : A.gear;
    /** A part's transform relative to its bone, from where it sits on the body in the bind pose (metres). */
    const local = (bone: GearBone, offset: number[], size: number[], turn: number[] = [0, 0, 0]): THREE.Matrix4 => {
      const b = tb[bone];
      b.getWorldPosition(v);
      const model = new THREE.Matrix4().compose(
        v.add(v2.fromArray(offset)),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(turn[0], turn[1], turn[2])),
        new THREE.Vector3(size[0], size[1], size[2]),
      );
      return b.matrixWorld.clone().invert().multiply(model);
    };
    const place = (name: GearName, bone: GearBone, offset: number[], size: number[]) => {
      this.gearLocal[name] = local(bone, offset, size);
      this.gearBone[name] = bone;
    };
    place('helmet', 'head', g.helmetOffset, [g.helmetScale, g.helmetScale, g.helmetScale]);
    place('stripe', 'head', g.helmetOffset, [g.helmetScale, g.helmetScale, g.helmetScale]);
    place('mask', 'head', g.helmetOffset, [g.helmetScale, g.helmetScale, g.helmetScale]);
    place('pads', 'chest', g.padsOffset, [g.padsScale, g.padsScale, g.padsScale]);
    place('numFront', 'chest', g.numberFrontOffset, [g.numberSize[0]!, g.numberSize[1]!, 1]);
    place('numBack', 'chest', g.numberBackOffset, [g.numberSize[0]!, g.numberSize[1]!, 1]);
    place('shoeL', 'footL', g.shoeOffset, [1, 1, 1]);
    place('shoeR', 'footR', [-g.shoeOffset[0]!, g.shoeOffset[1]!, g.shoeOffset[2]!], [1, 1, 1]);

    // Gear meshes, one instance per athlete.
    const shiny = new THREE.MeshPhongMaterial({ shininess: 90, specular: new THREE.Color(t.look.helmetSpecular) });
    shiny.onBeforeCompile = (s) => rimShader(s, t);
    const matte = new THREE.MeshLambertMaterial();
    matte.onBeforeCompile = (s) => rimShader(s, t);
    const gearParts: [GearName, THREE.BufferGeometry, THREE.Material][] = [
      ['helmet', helmetGeo(), shiny],
      ['stripe', stripeGeo(), shiny],
      ['mask', maskGeo(), matte],
      ['pads', padsGeo(), matte],
      ['shoeL', shoeGeo(t), matte],
      ['shoeR', shoeGeo(t), matte],
    ];
    for (const [name, geo, mat] of gearParts) this.addGear(name, geo, mat, total);
    // Still placed every frame (headPosition reads the helmet), just not drawn.
    if (hd) for (const [name] of gearParts) this.gear[name].visible = false;
    const atlas = numberAtlas(squads, t);
    for (const side of ['numFront', 'numBack'] as const) {
      const geo = new THREE.PlaneGeometry(1, 1);
      if (side === 'numBack') geo.rotateY(Math.PI);
      const offsets = new Float32Array(total * 2);
      for (let i = 0; i < total; i++) {
        offsets[i * 2] = (i % atlas.cols) / atlas.cols;
        offsets[i * 2 + 1] = 1 - (Math.floor(i / atlas.cols) + 1) / atlas.rows;
      }
      geo.setAttribute('instUv', new THREE.InstancedBufferAttribute(offsets, 2));
      const mat = new THREE.MeshLambertMaterial({ map: atlas.texture, transparent: true, alphaTest: 0.3 });
      mat.onBeforeCompile = (shader) => {
        rimShader(shader, t);
        shader.uniforms['uvScale'] = { value: new THREE.Vector2(1 / atlas.cols, 1 / atlas.rows) };
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nattribute vec2 instUv;\nuniform vec2 uvScale;')
          .replace('#include <uv_vertex>', '#include <uv_vertex>\nvMapUv = uv * uvScale + instUv;');
      };
      this.addGear(side, geo, mat, total);
    }
    // Brand logos on the kit: the wordmark across the back above the number, the mark on each side of the
    // helmet and, where shoulderMarks is set, on top of each shoulder pad (top of the art toward the front,
    // upright from behind; off for the HD athlete, whose pads move with its arms). Tinted with the kit's number colour.
    const L = g.logos;
    // Planes keep the artwork's own proportions: the wordmark is backSize[0] wide, the mark markSize tall.
    const word = [L.backSize[0]!, L.backSize[0]! / imageAspect(logos.word), 1];
    const mark = [L.markSize * imageAspect(logos.mark), L.markSize, 1];
    if (logos.word) this.logoSlots.push({ kind: 'word', bone: 'chest', local: local('chest', L.backOffset, word, [0, Math.PI, 0]) });
    if (logos.mark) {
      for (const side of [1, -1]) {
        const [hx, hy, hz] = L.helmetOffset as [number, number, number];
        const [sx, sy, sz] = L.shoulderOffset as [number, number, number];
        this.logoSlots.push({ kind: 'mark', bone: 'head', local: local('head', [hx * side, hy, hz], mark, [0, (side * Math.PI) / 2, 0]) });
        if (L.shoulderMarks) this.logoSlots.push({ kind: 'mark', bone: 'chest', local: local('chest', [sx * side, sy, sz], mark, [-Math.PI / 2, 0, Math.PI]) });
      }
    }
    for (const kind of ['word', 'mark'] as const) {
      const img = logos[kind];
      const count = this.logoSlots.filter((s) => s.kind === kind).length;
      if (!img || !count) continue;
      const mat = new THREE.MeshLambertMaterial({ map: logoTexture(img, t), transparent: true, alphaTest: L.alphaTest, depthWrite: false });
      mat.onBeforeCompile = (sh) => rimShader(sh, t);
      const mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mat, total * count);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = false;
      this.logoMeshes[kind] = mesh;
      this.group.add(mesh);
    }

    this.shadow = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(t.athlete.shadowYards, t.athlete.shadowYards).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ map: blobTexture(), transparent: true, depthWrite: false, opacity: t.athlete.shadowOpacity }),
      total,
    );
    this.shadow.frustumCulled = false;
    this.shadow.renderOrder = -1;
    this.group.add(this.shadow);

    // Bodies.
    const body = findBody(template);
    const baseMat = body.material as THREE.MeshStandardMaterial;
    const cuts = kitCuts(body, t);
    if (hd && !(baseMat.userData as Partial<HdExtras>).kitAverage?.parts) throw new Error('HD athlete model has no kit measurements');
    const hdCuts = hd ? hdBands(body, t) : null;
    const skinAverage = new THREE.Vector3().fromArray((baseMat.userData['skinAverage'] as number[] | undefined) ?? [1, 1, 1]);
    const c = new THREE.Color();
    let i = 0;
    for (const squad of squads) {
      this.offsets.push(i);
      const u = squad.uniform;
      for (let k = 0; k < squad.numbers.length; k++, i++) {
        const skin = t.look.skinTones[(i * 7 + 3) % t.look.skinTones.length]!;
        const kit = [skin, u.jersey, u.pants, u.jersey, u.stripe, t.look.cleat].map((hex, r) =>
          r === 0 ? new THREE.Color(hex) : liftDark(new THREE.Color(hex), t.look.blackLift),
        );
        const mat = hdCuts ? hdKitMaterial(model, kit, u, baseMat.userData as HdExtras, hdCuts, t) : kitMaterial(model, kit, skinAverage, cuts, t);
        this.materials.push(mat);
        const holder = new THREE.Group();
        const inst = cloneSkinned(template);
        inst.scale.setScalar(A.scale);
        inst.traverse((o) => {
          if ((o as THREE.SkinnedMesh).isSkinnedMesh) {
            const sm = o as THREE.SkinnedMesh;
            sm.material = mat;
            sm.frustumCulled = false;
          }
        });
        holder.add(inst);
        holder.visible = false;
        this.group.add(holder);
        const bones = findBones(inst);
        const mixer = new THREE.AnimationMixer(inst);
        const actions = {} as Record<Clip, THREE.AnimationAction>;
        const weights = {} as Record<Clip, number>;
        for (const cl of CLIPS) {
          const a = mixer.clipAction(clips[cl]);
          if (cl === 'jog' || cl === 'sprint') a.timeScale = 0;
          if (cl === 'fall') {
            a.setLoop(THREE.LoopOnce, 1);
            a.clampWhenFinished = true;
            a.timeScale = A.fallTimeScale;
          }
          a.play();
          a.setEffectiveWeight(0);
          actions[cl] = a;
          weights[cl] = 0;
        }
        this.list.push({
          holder,
          mixer,
          actions,
          weights,
          bones,
          x: 0,
          z: 0,
          px: 0,
          pz: 0,
          yaw: Math.PI,
          faceYaw: null,
          vx: 0,
          vz: 0,
          speed: 0,
          cycle: (i * 0.37) % 1,
          action: 'idle',
          actionT: 0,
          pitch: 0,
          lift: 0,
          danceYaw: 0,
          roll: 0,
          overlayL: 0,
          overlayR: 0,
          poseL: armPose(READY),
          poseR: armPose(READY),
          armClip: armBones(bones).map((b) => b.quaternion.clone()),
          visible: false,
        });
        this.branded.push(!!squad.branded);
        for (const mesh of Object.values(this.logoMeshes)) {
          const per = mesh.count / total;
          for (let k = 0; k < per; k++) mesh.setColorAt(i * per + k, c.set(u.numbers));
        }
        this.gear.helmet.setColorAt(i, liftDark(c.set(u.helmet), t.look.blackLift));
        this.gear.stripe.setColorAt(i, liftDark(c.set(u.stripe), t.look.blackLift));
        this.gear.mask.setColorAt(i, liftDark(c.set(u.facemask), t.look.blackLift));
        this.gear.pads.setColorAt(i, liftDark(c.set(u.jersey), t.look.blackLift));
        this.gear.shoeL.setColorAt(i, c.set(t.look.cleat));
        this.gear.shoeR.setColorAt(i, c.set(t.look.cleat));
      }
    }
    for (const mesh of [...Object.values(this.gear), ...Object.values(this.logoMeshes)]) if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }

  private addGear(name: GearName, geo: THREE.BufferGeometry, mat: THREE.Material, total: number): void {
    const mesh = new THREE.InstancedMesh(geo, mat, total);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    this.gear[name] = mesh;
    this.group.add(mesh);
  }

  index(squad: number, slot: number): number {
    return (this.offsets[squad] ?? 0) + slot;
  }

  place(i: number, x: number, z: number, action?: Action, face?: number | null): void {
    const a = this.list[i];
    if (!a) return;
    if (!a.visible) {
      a.px = x;
      a.pz = z;
      a.vx = a.vz = 0;
    }
    a.visible = true;
    a.x = x;
    a.z = z;
    if (face !== undefined) a.faceYaw = face;
    if (action && action !== a.action) this.setAction(a, action);
  }

  teleport(i: number, x: number, z: number, action: Action, face: number): void {
    const a = this.list[i];
    if (!a) return;
    a.x = a.px = x;
    a.z = a.pz = z;
    a.yaw = face;
    a.faceYaw = face;
    a.vx = a.vz = a.speed = 0;
    a.visible = true;
    this.setAction(a, action);
    // Land in the pose at once: no blend in from wherever the last play ended.
    const w = this.targetWeights(a);
    for (const c of CLIPS) a.weights[c] = w[c];
    const o = this.overlay(a);
    a.overlayL = o.left ? 1 : 0;
    a.overlayR = o.right ? 1 : 0;
    if (o.left) setPose(a.poseL, o.left);
    if (o.right) setPose(a.poseR, o.right);
    a.pitch = 0;
    a.lift = 0;
    a.danceYaw = 0;
    a.roll = 0;
  }

  private setAction(a: Rigged, action: Action): void {
    a.action = action;
    a.actionT = 0;
    if (action === 'down') a.actions.fall.reset().play();
  }

  hide(i: number): void {
    const a = this.list[i];
    if (a) a.visible = false;
  }

  actionOf(i: number): Action {
    return this.list[i]?.action ?? 'idle';
  }

  handPosition(i: number, side: 0 | 1, out: THREE.Vector3): THREE.Vector3 {
    const a = this.list[i];
    if (!a) return out.set(0, 0, 0);
    const b = a.bones;
    (side === 0 ? b.handL : b.handR).getWorldPosition(out);
    (side === 0 ? b.palmL : b.palmR).getWorldPosition(v);
    return out.add(v).multiplyScalar(0.5);
  }

  facing(i: number): number {
    const a = this.list[i];
    return a ? a.yaw + a.danceYaw : 0;
  }

  headPosition(i: number, out: THREE.Vector3): THREE.Vector3 {
    this.gear.helmet.getMatrixAt(i, this.m);
    return out.set(0, this.t.athlete.helmetTop, 0).applyMatrix4(this.m);
  }

  update(dt: number): void {
    const A = this.t.athlete;
    const kb = 1 - Math.exp(-A.blendRate * dt);
    const ko = 1 - Math.exp(-A.overlayRate * dt);
    const kp = 1 - Math.exp(-A.poseRate * dt);
    for (let i = 0; i < this.list.length; i++) {
      const a = this.list[i]!;
      a.holder.visible = a.visible;
      if (!a.visible) {
        this.writeGear(i, null);
        continue;
      }
      const dx = a.x - a.px;
      const dz = a.z - a.pz;
      const dist = Math.hypot(dx, dz);
      stepHeading(a, dx, dz, dt, a.faceYaw, a.action === 'block' || a.action === 'down', A);
      a.px = a.x;
      a.pz = a.z;
      a.actionT += dt;

      // Clips: weights ease toward the action's mix; the run cycle follows the ground covered.
      const target = this.targetWeights(a);
      let sum = 0;
      for (const c of CLIPS) {
        a.weights[c] += (target[c] - a.weights[c]) * kb;
        sum += a.weights[c];
      }
      const sprintMix = this.sprintMix(a.speed);
      a.cycle = (a.cycle + dist / THREE.MathUtils.lerp(this.strideJog, this.strideSprint, sprintMix)) % 1;
      for (const c of CLIPS) a.actions[c].setEffectiveWeight(sum > 0 ? a.weights[c] / sum : c === 'idle' ? 1 : 0);
      a.actions.jog.time = a.cycle * a.actions.jog.getClip().duration;
      a.actions.sprint.time = a.cycle * a.actions.sprint.getClip().duration;
      // The mixer only writes a bone whose clip value changed, so put back the clip pose under last frame's aim.
      const arms = armBones(a.bones);
      for (let k = 0; k < 4; k++) arms[k]!.quaternion.copy(a.armClip[k]!);
      a.mixer.update(dt);
      for (let k = 0; k < 4; k++) a.armClip[k]!.copy(arms[k]!.quaternion);

      // Whole body: dives tip forward from the feet; a scorer dances.
      let pitch = 0;
      let lift = 0;
      let yaw = 0;
      let roll = 0;
      if (a.action === 'dive') {
        pitch = A.divePitch * Math.min(1, a.actionT / A.diveSeconds);
        lift = A.diveLiftYards * Math.sin(Math.min(1, a.actionT / A.diveLiftSeconds) * Math.PI);
      } else if (a.action === 'celebrate') {
        const d = danceAt(a.actionT, A.dance);
        pitch = d.pitch;
        lift = d.lift;
        yaw = d.yaw;
        roll = d.roll;
      }
      const dancing = a.action === 'celebrate';
      // The dance sets the body outright (it is smooth already); everything else eases.
      a.pitch = dancing ? pitch : a.pitch + (pitch - a.pitch) * kp;
      a.lift = dancing ? lift : a.lift + (lift - a.lift) * kp;
      a.danceYaw = dancing ? yaw : a.danceYaw * (1 - kp);
      a.roll = dancing ? roll : a.roll * (1 - kp);
      a.holder.position.set(a.x, a.lift, a.z);
      euler.set(a.pitch, a.yaw + a.danceYaw, a.roll, 'YXZ');
      a.holder.quaternion.setFromEuler(euler);
      a.holder.updateMatrixWorld(true);

      // Arms aimed on top of the clip.
      const o = this.overlay(a);
      if (o.left) easePose(a.poseL, o.left, a.overlayL > 0.01 ? ko : 1);
      if (o.right) easePose(a.poseR, o.right, a.overlayR > 0.01 ? ko : 1);
      a.overlayL += ((o.left ? 1 : 0) - a.overlayL) * ko;
      a.overlayR += ((o.right ? 1 : 0) - a.overlayR) * ko;
      if (a.overlayL > 0.01 || a.overlayR > 0.01) {
        qHolder.copy(a.holder.quaternion);
        a.bones.chest.getWorldQuaternion(qTorso).multiply(this.chestBindInv);
        if (a.overlayL > 0.01) this.aimArm(a.bones.upL, a.bones.loL, this.axisUpL, this.axisLoL, a.poseL, 1, a.overlayL);
        if (a.overlayR > 0.01) this.aimArm(a.bones.upR, a.bones.loR, this.axisUpR, this.axisLoR, a.poseR, -1, a.overlayR);
      }
      this.writeGear(i, a);
    }
    for (const mesh of [...Object.values(this.gear), ...Object.values(this.logoMeshes)]) mesh.instanceMatrix.needsUpdate = true;
    this.shadow.instanceMatrix.needsUpdate = true;
  }

  private sprintMix(speed: number): number {
    return THREE.MathUtils.smoothstep(speed, this.speedJog, this.speedSprint);
  }

  private targetWeights(a: Rigged): Record<Clip, number> {
    const A = this.t.athlete;
    const w: Record<Clip, number> = { idle: 0, jog: 0, sprint: 0, crouch: 0, push: 0, fall: 0 };
    const moving = a.speed > A.movingYps;
    const run = () => {
      const s = this.sprintMix(a.speed);
      w.jog = 1 - s;
      w.sprint = s;
    };
    switch (a.action) {
      case 'down':
        w.fall = 1;
        break;
      case 'block':
        w.push = 1;
        break;
      case 'dive':
        run();
        break;
      case 'stance':
        if (moving) run();
        else {
          w.crouch = A.stanceCrouch;
          w.idle = 1 - A.stanceCrouch;
        }
        break;
      case 'lineStance':
        if (moving) run();
        else w.crouch = 1;
        break;
      case 'qbReady':
        if (moving) run();
        else {
          w.crouch = A.qbCrouch;
          w.idle = 1 - A.qbCrouch;
        }
        break;
      case 'run':
      case 'carry':
        if (moving) run();
        else w.idle = 1;
        break;
      default:
        if (moving) run();
        else w.idle = 1;
    }
    return w;
  }

  private overlay(a: Rigged): Overlay {
    const A = this.t.athlete;
    const moving = a.speed > A.movingYps;
    switch (a.action) {
      case 'carry':
        return { left: TUCK, right: null };
      case 'down':
        return { left: TUCK, right: null };
      case 'qbReady':
        return moving ? NO_OVERLAY : { left: READY, right: READY };
      case 'lineStance':
        return moving ? NO_OVERLAY : { left: null, right: HAND_DOWN };
      case 'block':
        return { left: BLOCK, right: BLOCK };
      case 'catch':
        return { left: HANDS_UP, right: HANDS_UP };
      case 'reach':
        return { left: REACH, right: REACH };
      case 'dive':
        return { left: DIVE, right: DIVE };
      case 'celebrate': {
        const d = danceAt(a.actionT, A.dance);
        return { left: DANCE_ARMS[d.left], right: DANCE_ARMS[d.right] };
      }
      case 'throw': {
        const tt = a.actionT;
        const right = tt < A.throwWindSeconds ? THROW_WIND : tt < A.throwReleaseSeconds ? THROW_RELEASE : THROW_FOLLOW;
        return { left: GUIDE_HAND, right };
      }
      default:
        return NO_OVERLAY;
    }
  }

  /** Turns the upper arm, then the forearm, toward body-space directions (side +1 left, -1 right). */
  private aimArm(up: THREE.Bone, lo: THREE.Bone, axisUp: THREE.Vector3, axisLo: THREE.Vector3, pose: ArmPose, side: number, w: number): void {
    const frame = pose.ground ? qHolder : qTorso;
    this.aimBone(up, axisUp, pose.up, side, w, frame);
    this.aimBone(lo, axisLo, pose.lo, side, w, frame);
  }

  private aimBone(bone: THREE.Bone, axis: THREE.Vector3, dir: THREE.Vector3, side: number, w: number, frame: THREE.Quaternion): void {
    const parent = bone.parent;
    if (!parent) return;
    v.set(dir.x * side, dir.y, dir.z).normalize().applyQuaternion(frame);
    bone.getWorldQuaternion(qa);
    v2.copy(axis).applyQuaternion(qa);
    qb.setFromUnitVectors(v2, v).multiply(qa);
    parent.getWorldQuaternion(qc);
    qb.premultiply(qc.invert());
    bone.quaternion.slerp(qb, w);
    bone.updateMatrixWorld(true);
  }

  private writeGear(i: number, a: Rigged | null): void {
    this.writeLogos(i, a);
    if (!a) {
      for (const mesh of Object.values(this.gear)) mesh.setMatrixAt(i, this.hidden);
      this.shadow.setMatrixAt(i, this.hidden);
      return;
    }
    for (const name of Object.keys(this.gear) as GearName[]) {
      const bone = a.bones[this.gearBone[name]];
      this.m.multiplyMatrices(bone.matrixWorld, this.gearLocal[name]);
      this.gear[name].setMatrixAt(i, this.m);
    }
    const s = a.action === 'down' || a.action === 'dive' ? this.t.athlete.shadowDownScale : 1;
    this.m.makeScale(s, 1, s).setPosition(a.x, this.t.athlete.shadowLiftYards, a.z);
    this.shadow.setMatrixAt(i, this.m);
  }

  private writeLogos(i: number, a: Rigged | null): void {
    const next: Partial<Record<LogoKind, number>> = {};
    for (const slot of this.logoSlots) {
      const mesh = this.logoMeshes[slot.kind];
      if (!mesh) continue;
      const per = mesh.count / this.list.length;
      const k = next[slot.kind] ?? 0;
      next[slot.kind] = k + 1;
      if (!a || !this.branded[i]) mesh.setMatrixAt(i * per + k, this.hidden);
      else mesh.setMatrixAt(i * per + k, this.m.multiplyMatrices(a.bones[slot.bone].matrixWorld, slot.local));
    }
  }

  dispose(): void {
    for (const mesh of [...Object.values(this.gear), ...Object.values(this.logoMeshes), this.shadow]) {
      mesh.geometry.dispose();
      const mat = mesh.material as THREE.MeshLambertMaterial;
      mat.map?.dispose();
      mat.dispose();
    }
    for (const m of this.materials) m.dispose();
    for (const a of this.list) a.mixer.stopAllAction();
  }
}

/** Shoulder pads in metres, centred on the shoulder line: a raised cap over each shoulder on a chest and back shell. */
function padsGeo(): THREE.BufferGeometry {
  const cap = (side: number) => new THREE.SphereGeometry(0.13, 12, 8).scale(1.05, 0.7, 1.2).translate(side * 0.16, 0.01, 0).toNonIndexed();
  const shell = new THREE.SphereGeometry(0.17, 14, 8).scale(1.25, 0.7, 0.85).translate(0, 0, 0).toNonIndexed();
  return mergeGeometries([cap(1), cap(-1), shell])!;
}

/** A cleat in metres: a rounded block from heel to toe, sole on y 0, toe toward +z. */
function shoeGeo(t: Tunables): THREE.BufferGeometry {
  const [w, h, l] = t.athlete.gear.shoeSize as [number, number, number];
  return new THREE.CapsuleGeometry(w / 2, l - w, 4, 10).rotateX(Math.PI / 2).scale(1, h / w, 1).translate(0, h / 2, 0);
}

function armPose(aim: ArmAim): ArmPose {
  return setPose({ up: new THREE.Vector3(), lo: new THREE.Vector3(), ground: false }, aim);
}

function setPose(p: ArmPose, aim: ArmAim): ArmPose {
  p.up.fromArray(aim.up).normalize();
  p.lo.fromArray(aim.lo).normalize();
  p.ground = !!aim.ground;
  return p;
}

/** Moves an arm's aim part of the way (k) toward a new one; directions stay unit length. */
function easePose(p: ArmPose, aim: ArmAim, k: number): void {
  p.up.lerp(v.fromArray(aim.up).normalize(), k).normalize();
  p.lo.lerp(v.fromArray(aim.lo).normalize(), k).normalize();
  p.ground = !!aim.ground;
}

function armBones(b: Bones): [THREE.Bone, THREE.Bone, THREE.Bone, THREE.Bone] {
  return [b.upL, b.loL, b.upR, b.loR];
}

/** Width over height of a loaded image, 1 when it is missing or has no size. */
function imageAspect(img: HTMLImageElement | null): number {
  return img && img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
}

/** The logo artwork as a white texture (tinted per kit by the instance colour), at a fixed height in pixels. */
function logoTexture(img: HTMLImageElement, t: Tunables): THREE.CanvasTexture {
  const h = t.athlete.gear.logos.texturePx;
  const w = Math.max(1, Math.round(imageAspect(img) * h));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas not available');
  ctx.drawImage(img, 0, 0, w, h);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, w, h);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = t.render.anisotropy;
  return tex;
}

function findBones(root: THREE.Object3D): Bones {
  const get = (name: string): THREE.Bone => {
    const o = root.getObjectByName(name);
    if (!o || !(o as THREE.Bone).isBone) throw new Error(`athlete model has no ${name} bone`);
    return o as THREE.Bone;
  };
  return {
    head: get('Head'),
    chest: get('spine_03'),
    footL: get('foot_l'),
    footR: get('foot_r'),
    upL: get('upperarm_l'),
    loL: get('lowerarm_l'),
    handL: get('hand_l'),
    palmL: get('middle_01_l'),
    upR: get('upperarm_r'),
    loR: get('lowerarm_r'),
    handR: get('hand_r'),
    palmR: get('middle_01_r'),
  };
}

function findBody(root: THREE.Object3D): THREE.SkinnedMesh {
  let body: THREE.SkinnedMesh | null = null;
  root.traverse((o) => {
    if (!body && (o as THREE.SkinnedMesh).isSkinnedMesh) body = o as THREE.SkinnedMesh;
  });
  if (!body) throw new Error('athlete model has no skinned mesh');
  return body;
}

/**
 * Kit seams as cuts through the bind pose (T-pose), in the mesh's own coordinates: sleeves and gloves by
 * distance from the spine along the arms, collar, waist, knee and sock line by height. Straight seams,
 * whatever the triangles.
 */
function kitCuts(body: THREE.SkinnedMesh, t: Tunables): { a: THREE.Vector4; b: THREE.Vector4 } {
  const c = t.athlete.kitCuts;
  const bones = body.skeleton.bones;
  const at = (name: string): THREE.Vector3 => {
    const j = bones.findIndex((b) => b.name === name);
    if (j < 0) throw new Error(`athlete model has no ${name} bone`);
    return new THREE.Vector3()
      .setFromMatrixPosition(body.skeleton.boneInverses[j]!.clone().invert())
      .applyMatrix4(body.bindMatrixInverse);
  };
  const lerp = (p: string, q: string, f: number) => at(p).lerp(at(q), f);
  const cx = at('pelvis').x;
  return {
    a: new THREE.Vector4(
      cx,
      Math.abs(lerp('upperarm_l', 'lowerarm_l', c.sleeve).x - cx),
      Math.abs(lerp('lowerarm_l', 'hand_l', c.glove).x - cx),
      lerp('spine_03', 'neck_01', c.collar).y,
    ),
    b: new THREE.Vector4(lerp('pelvis', 'spine_01', c.waist).y, lerp('calf_l', 'foot_l', c.knee).y, lerp('calf_l', 'foot_l', c.sock).y, 0),
  };
}

/** What tools/athlete-hd.mjs measured on the HD texture (linear colours). */
interface HdExtras {
  skinAverage: number[];
  /** Accent and grey: average colours. Parts: average brightness of the dark texels on helmet, glove, jersey, pants, socks, cleat. */
  kitAverage: { accent: number[]; grey: number[]; parts: number[] };
}

/**
 * The HD body's parts in its bind space, from its joints (as kitCuts does): above the neck is helmet, past the
 * wrists gloves, then jersey down to the hips, pants to the knees, socks to the ankles, cleats below. The build
 * (tools/athlete-hd.mjs) splits the body the same way.
 */
function hdBands(body: THREE.SkinnedMesh, t: Tunables): { bands: THREE.Vector4; wrist: number } {
  const bones = body.skeleton.bones;
  const at = (name: string): THREE.Vector3 => {
    const j = bones.findIndex((b) => b.name === name);
    if (j < 0) throw new Error(`athlete model has no ${name} bone`);
    return new THREE.Vector3().setFromMatrixPosition(body.skeleton.boneInverses[j]!.clone().invert()).applyMatrix4(body.bindMatrixInverse);
  };
  const cx = at('pelvis').x;
  return {
    bands: new THREE.Vector4(at('neck_01').y, at('pelvis').y, at('calf_l').y, at('foot_l').y),
    wrist: Math.abs(at('lowerarm_l').lerp(at('hand_l'), t.athlete.hd.gloveAt).x - cx),
  };
}

/**
 * The HD athlete's kit, repainted per team from its own texture. Each texel is sorted into accent trim, skin,
 * grey (the facemask) or dark (the kit), the same test the build used (tunables athlete.hd.classify). Skin
 * is retinted to the athlete's tone; kit texels take the team colour for that part of the body, keeping the
 * texture's shading relative to that kind's average brightness.
 */
function hdKitMaterial(
  textures: Pick<AthleteModel, 'skin' | 'normal'>,
  kit: THREE.Color[],
  u: SquadSpec['uniform'],
  x: HdExtras,
  cuts: { bands: THREE.Vector4; wrist: number },
  t: Tunables,
): THREE.MeshLambertMaterial {
  const H = t.athlete.hd;
  const c = H.classify;
  const lift = (hex: string) => liftDark(new THREE.Color(hex), t.look.blackLift);
  const mat = new THREE.MeshLambertMaterial({ map: textures.skin, normalMap: textures.normal });
  mat.normalScale.set(1, -1);
  const lum = (a: number[]) => 0.2126 * a[0]! + 0.7152 * a[1]! + 0.0722 * a[2]!;
  mat.onBeforeCompile = (shader) => {
    rimShader(shader, t);
    const uniforms: Record<string, unknown> = {
      uSkin: kit[0],
      uSkinAvg: new THREE.Vector3().fromArray(x.skinAverage),
      uHelmet: lift(u.helmet),
      uMask: lift(u.facemask),
      uJersey: kit[1],
      uPants: kit[2],
      uSocks: kit[3],
      uStripe: kit[4],
      uCleat: kit[5],
      uLum: new THREE.Vector2(lum(x.kitAverage.accent), lum(x.kitAverage.grey)),
      uPartLum: x.kitAverage.parts,
      uBands: cuts.bands,
      uWrist: cuts.wrist,
      uShadeMax: H.shadeMax,
      uAccent: new THREE.Vector3(c.accentMin, c.accentGreenOverRed, c.accentBlueOverGreen),
      uSkinTest: new THREE.Vector4(c.skinMin, c.skinBlueOverGreen, c.skinSaturationMin, 0),
      uGrey: new THREE.Vector2(c.greyMin, c.greySaturationMax),
    };
    for (const [k, value] of Object.entries(uniforms)) shader.uniforms[k] = { value };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vBind;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvBind = position;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        varying vec3 vBind;
        uniform vec3 uSkin, uSkinAvg, uHelmet, uMask, uJersey, uPants, uSocks, uStripe, uCleat, uAccent, uSkinTest;
        uniform vec4 uBands;
        uniform vec2 uGrey, uLum;
        uniform float uPartLum[6];
        uniform float uWrist, uShadeMax;
        float kitLum(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }`,
      )
      .replace(
        '#include <map_fragment>',
        `// Parts: helmet, glove, jersey, pants, socks, cleat (the order of uPartLum).
        int partIx = vBind.y > uBands.x ? 0 : abs(vBind.x) > uWrist ? 1 : vBind.y > uBands.y ? 2 : vBind.y > uBands.z ? 3 : vBind.y > uBands.w ? 4 : 5;
        vec3 part = partIx == 0 ? uHelmet : partIx == 3 ? uPants : partIx == 4 ? uSocks : partIx == 5 ? uCleat : uJersey;
        #ifdef USE_MAP
          vec3 tex = texture2D(map, vMapUv).rgb;
          float partLum = uPartLum[0];
          for (int k = 1; k < 6; k++) if (k == partIx) partLum = uPartLum[k];
          float mx = max(tex.r, max(tex.g, tex.b));
          float sat = mx > 0.0 ? (mx - min(tex.r, min(tex.g, tex.b))) / mx : 0.0;
          vec3 kitCol;
          if (mx > uAccent.x && tex.g > tex.r * uAccent.y && tex.b < tex.g * uAccent.z) kitCol = uStripe * min(kitLum(tex) / uLum.x, uShadeMax);
          else if (mx > uSkinTest.x && tex.r > tex.g && tex.g > tex.b && tex.b > tex.g * uSkinTest.y && sat > uSkinTest.z) kitCol = uSkin * tex / uSkinAvg;
          else if (mx > uGrey.x && sat < uGrey.y) kitCol = (partIx == 0 ? uMask : part) * min(kitLum(tex) / uLum.y, uShadeMax);
          else kitCol = part * min(kitLum(tex) / partLum, uShadeMax);
        #else
          // No texture (it failed to load): flat team colours by part.
          vec3 kitCol = part;
        #endif
        diffuseColor.rgb *= kitCol;`,
      );
  };
  return mat;
}

/**
 * The body material: the skin texture retinted to the player's skin tone, flat kit colours elsewhere
 * (the muscle normal map is mostly flattened on cloth), plus the stadium rim light. Without the textures
 * (they failed to load) it still compiles: flat skin tones, no muscle relief.
 */
function kitMaterial(
  textures: Pick<AthleteModel, 'skin' | 'normal'>,
  kit: THREE.Color[],
  skinAverage: THREE.Vector3,
  cuts: { a: THREE.Vector4; b: THREE.Vector4 },
  t: Tunables,
): THREE.MeshLambertMaterial {
  const A = t.athlete;
  // Lambert, like the rest of the athletes: matte kits that stay black at night, and cheaper on phones.
  const mat = new THREE.MeshLambertMaterial({ map: textures.skin, normalMap: textures.normal });
  // A glTF normal map on a model without tangents needs y flipped in three (as GLTFLoader does), or the muscles light from below.
  mat.normalScale.set(1, -1);
  mat.onBeforeCompile = (shader) => {
    rimShader(shader, t);
    shader.uniforms['uKit'] = { value: kit };
    shader.uniforms['uSkinAvg'] = { value: skinAverage };
    shader.uniforms['uKitFlatten'] = { value: A.kitNormalFlatten };
    shader.uniforms['uCutA'] = { value: cuts.a };
    shader.uniforms['uCutB'] = { value: cuts.b };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vBind;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvBind = position;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>\nvarying vec3 vBind;\nuniform vec3 uKit[${KIT_REGIONS}];\nuniform vec3 uSkinAvg;\nuniform float uKitFlatten;\nuniform vec4 uCutA;\nuniform vec4 uCutB;`,
      )
      .replace(
        '#include <map_fragment>',
        `// 0 skin, 1 jersey, 2 pants, 3 socks, 4 glove, 5 cleat.
        float kitArm = abs(vBind.x - uCutA.x);
        int kitRegion = 5;
        if (kitArm > uCutA.z) kitRegion = 4;
        else if (kitArm > uCutA.y || vBind.y > uCutA.w) kitRegion = 0;
        else if (vBind.y > uCutB.x) kitRegion = 1;
        else if (vBind.y > uCutB.y) kitRegion = 2;
        else if (vBind.y > uCutB.z) kitRegion = 3;
        #ifdef USE_MAP
          vec3 kitSkin = uKit[0] * texture2D(map, vMapUv).rgb / uSkinAvg;
        #else
          vec3 kitSkin = uKit[0];
        #endif
        diffuseColor.rgb *= kitRegion == 0 ? kitSkin : uKit[kitRegion];`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        '#include <normal_fragment_maps>\nif (kitRegion > 0) normal = normalize(mix(normal, nonPerturbedNormal, uKitFlatten));',
      );
  };
  return mat;
}
